import { ref } from "vue";
import { useI18n } from "vue-i18n";

import config from "@/config";
import { useNotification } from "@/hooks/notifications";

import type { Ref } from "vue";

type WindowWithGoogle = Window &
  typeof globalThis & {
    recaptchaSuccessCallback?: (token: string) => void;
    recaptchaErrorCallback?: (error: string) => void;
    recaptchaExpiredCallback?: () => void;
  };

export const useRecaptcha = (): {
  initRecaptcha: () => void;
  removeRecaptcha: () => void;
  recaptchaPassed: Ref<boolean>;
  recaptchaSiteKey: string;
} => {
  const { showMessage } = useNotification();
  const { t } = useI18n();
  const recaptchaPassed = ref(false);
  const recaptchaSiteKey = config.google.recaptchaSiteKey || "";

  const initRecaptcha = () => {
    const recaptchaScript = document.createElement("script");
    recaptchaScript.src = "https://www.google.com/recaptcha/api.js";
    recaptchaScript.async = true;
    recaptchaScript.defer = true;
    recaptchaScript.id = "recaptchaScript";
    document.head.appendChild(recaptchaScript);
  };

  const removeRecaptcha = () => {
    const recaptchaScript = document.getElementById("recaptchaScript");
    recaptchaScript?.parentNode?.removeChild(recaptchaScript);
    // There are additional scripts that google injects into DOM so we iterate and remove them
    const headScripts = document.head.getElementsByTagName("script");
    Array.from(headScripts).forEach((script: HTMLScriptElement) => {
      if (script.src.includes("recaptcha")) {
        script.parentNode?.removeChild(script);
      }
    });
    if ((window as WindowWithGoogle).recaptchaSuccessCallback) {
      delete (window as WindowWithGoogle).recaptchaSuccessCallback;
    }
    if ((window as WindowWithGoogle).recaptchaErrorCallback) {
      delete (window as WindowWithGoogle).recaptchaErrorCallback;
    }
    if ((window as WindowWithGoogle).recaptchaExpiredCallback) {
      delete (window as WindowWithGoogle).recaptchaExpiredCallback;
    }
  };

  (window as WindowWithGoogle).recaptchaSuccessCallback = (token) => {
    if (token) {
      recaptchaPassed.value = true;
    }
  };

  (window as WindowWithGoogle).recaptchaExpiredCallback = () => {
    recaptchaPassed.value = false;
  };

  (window as WindowWithGoogle).recaptchaErrorCallback = () => {
    recaptchaPassed.value = false;
    showMessage(
      t("COMMON.NETWORK_ERROR"),
      "error",
      t("COMMON.NETWORK_ERROR_SUBTITLE")
    );
  };

  return {
    initRecaptcha,
    removeRecaptcha,
    recaptchaPassed,
    recaptchaSiteKey,
  };
};
